import { SelectionModel } from '@angular/cdk/collections';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatInput } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TABLE_DATA_TYPE, TABLE_PAGE_SIZE_OPTIONS } from '@app/shared/constants/table';
import { ExportTableService } from '@app/shared/helpers/export-table.service';
import {
    TableColumn,
    TableFilters,
    TableExportConfiguration,
    TableRow,
    TableRowAction,
    TableRowActionEvent,
    TableRowClickEvent,
} from '@app/shared/models/table.model';
import { get } from 'lodash';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    providers: [ExportTableService],
    standalone: false,
})
export class TableComponent implements OnInit, OnChanges {
    @ViewChild(MatPaginator, { static: false }) matPaginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) matSort: MatSort;
    @ViewChild('searchInput') searchInput: MatInput;

    @Input() actions: TableRowAction[] = [];
    @Input() columns: TableColumn[] = [];
    @Input() set dataSource(dataSource: MatTableDataSource<TableRow>) {
        this.setTableDataSource(dataSource ?? new MatTableDataSource<TableRow>());
    }
    @Input() allowMultiSelect: boolean;
    @Input() allowFiltering = true;
    @Input() allowDownloading = false;
    @Input() allowRowClick = false;
    @Input() disableSortClear = true;
    @Input() isCard = true;
    @Input() useTablePagination = true;
    @Input() containerHeight = 'unset';
    @Input() externalFilters = undefined;
    @Input() exportConfiguration: TableExportConfiguration = undefined;

    @Output() executeAction: EventEmitter<TableRowActionEvent> =
        new EventEmitter<TableRowActionEvent>();
    @Output() selectionChange: EventEmitter<unknown[]> = new EventEmitter<unknown[]>();
    @Output() rowClick: EventEmitter<TableRowClickEvent> = new EventEmitter<TableRowClickEvent>();

    tableDataSource = new MatTableDataSource([]);
    displayedColumns: string[];
    pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;
    pageSize = TABLE_PAGE_SIZE_OPTIONS[0];
    actionsWidth: string;
    tableDataTypes = TABLE_DATA_TYPE;
    searchText = '';
    filters: TableFilters;
    selection: SelectionModel<unknown>;
    showRowActions: boolean;
    isContextMenuClick = false;

    private initialSelection = [];

    constructor(private exportTableService: ExportTableService) {
        this.filters = {
            text: '',
            stat: undefined,
        };
    }

    ngOnInit(): void {
        this.actionsWidth = '55px';
        this.showRowActions = this.actions.length > 0;
        this.displayedColumns = [
            ...(this.allowMultiSelect ? ['select'] : []),
            ...this.columns.map((tableColumn: TableColumn) => tableColumn.key),
            ...(this.actions.length ? ['actions'] : []),
        ];
        this.selection = new SelectionModel<unknown>(this.allowMultiSelect, this.initialSelection);
        if (this.allowDownloading && this.exportConfiguration) {
            this.exportTableService.configure(this.exportConfiguration);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.externalFilters) {
            this.filters = {
                ...this.filters,
                stat: changes.externalFilters.currentValue as number,
            };
            this.applyFilter();
        }
    }

    setTableDataSource(dataSource: MatTableDataSource<TableRow>): void {
        this.searchText = '';
        this.tableDataSource = dataSource;
        this.tableDataSource.sort = this.matSort;

        if (this.useTablePagination) {
            this.tableDataSource.paginator = this.matPaginator;
        }

        if (this.selection) {
            this.selection.clear();
        }

        setTimeout(() => {
            this.tableDataSource.paginator = this.matPaginator;
        });
    }

    applyFilter(): void {
        this.filters = {
            ...this.filters,
            text: this.searchText.trim().toLowerCase(),
        };
        this.tableDataSource.filter = JSON.stringify(this.filters);
    }

    onContextMenuToggle(element: unknown, isOpen: boolean): void {
        this.isContextMenuClick = true;
        element['actionsVisible'] = isOpen;
    }

    onRowClick(row: unknown, event: MouseEvent) {
        if (this.isContextMenuClick) {
            this.isContextMenuClick = false;
            return;
        }

        if (this.allowRowClick) {
            this.rowClick.emit({
                element: get(row, 'element'),
                target: event.target as HTMLElement,
            });
        }
    }

    execute(action: TableRowAction, element: unknown): void {
        element['actionsVisible'] = false;
        this.executeAction.emit({ key: action.key, element });
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.tableDataSource.data.length;
        return numSelected === numRows;
    }

    masterToggle(): void {
        this.isAllSelected()
            ? this.selection.clear()
            : this.tableDataSource.data.forEach((row) => this.selection.select(row));

        this.emitSelectedElements();
    }

    rowSelectionToggle(row: unknown): void {
        this.selection.toggle(row);
        this.emitSelectedElements();
    }

    private emitSelectedElements(): void {
        /* eslint-disable-next-line */
        const selected = this.selection.selected.map((item: any) => item.element);
        this.selectionChange.emit(selected);
    }

    trackByFn(_index: number, item: TableColumn): string {
        return item.key.toString();
    }

    export() {
        this.exportTableService.export(this.tableDataSource.filteredData);
    }
}
